import { Component, Host, Prop, State, Watch, h } from '@stencil/core';

import { isEnterKey, renderResponsiveImages2 } from '../../helpers/helpers';
import type {
  ImageParams,
  ResponsiveImages,
  Tags,
} from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-key-visual',
  styleUrl: 'cms-key-visual.scss',
  shadow: true,
})
export class CmsKeyVisual {
  private imageParams: ImageParams = {
    responsiveImages: null,
    imageSizes: null,
    altText: null,
    imageClass: null,
    imageStyles: null,
    lazyLoad: false,
  };

  private readonly imageSizes = `
  (max-width: 767px) and (min-resolution: 288dpi) 50vw,
  (max-width: 767px) and (min-resolution: 192dpi) 500vw,
  (max-width: 767px) and (min-resolution: 96dpi) 100vw,
  (min-width: 768px) and (max-width: 1440px) and (min-resolution: 288dpi) 50vw,
  (min-width: 768px) and (max-width: 1440px) and (min-resolution: 192dpi) 50vw,
  (min-width: 768px) and (max-width: 1440px) and (min-resolution: 96dpi) 50vw,
  (min-width: 1440px) 700px,
  100vw`;

  private startYtVideo: boolean;

  @State() loadVideo: boolean;

  /**
   * set the usercentrics consent status
   */
  @Prop({ reflect: true }) isConsent: boolean;

  /**
   * global properties
   * Set the PageType like standard, manual
   */
  @Prop() pageType: string;

  /**
   * Set the tags like #Pool
   */
  @Prop() keywords: Tags[] | string;

  /**
   * Set the Headline
   */
  @Prop() headline: string;

  /**
   *  Set the Headline Level like h1, h2
   */
  @Prop() headlineLevel: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  /**
   * Set the Badge Nam
   */
  @Prop() badgeName: string;

  /**
   * Set the top-image e.g. the ''-Logo. Badge is ignored if topImage is provided
   */
  @Prop() topImage: string;

  /**
   * Set the top-image for mobile. Fallback to topImage if not defined
   */
  @Prop() topImageMobile: string;

  /**
   * Set the Text
   */
  @Prop() pageText: string;

  //require by pageType standard
  /**
   * Set the responsive Images
   */
  @Prop() images: ResponsiveImages | string;

  /**
   * Set the alt title text
   */
  @Prop() imageAltText: string;

  /**
   * Set the cms-text-button-link target url
   */
  @Prop() ctaUrl: string;

  /**
   * Set the cms-text-button-link  label
   */
  @Prop() ctaLabel: string;

  /**
   * Set the cms-text-button-link button text
   */
  @Prop() ctaButtonText: string;

  //require by pageType manual
  /**
   * Set the media type
   */
  @Prop() mediaType: 'image' | 'yt-video';

  /**
   * Set the youtube video url
   */
  @Prop() ytVideoParam: string;

  /**
   * Set the youtube Video title
   */
  @Prop() ytVideoTitle = 'Hornbach Youtube Video';

  /**
   * is true when displayed in preview mode
   */
  @Prop() preview: boolean;

  /**
   *  Set the youtube video css class
   */
  @Prop() ytVideoClass: string;

  /**
   * Set the Challenge level like 1-5
   */
  @Prop() challenge: number;

  /**
   * Set the Challenge label
   */
  @Prop() challengeLabel: string;

  /**
   * Set the time to build the project like 10 minutes
   */
  @Prop() buildTime: string;

  /**
   * Set the build time label
   */
  @Prop() buildTimeLabel: string;

  /**
   * Set the number of doer to build the project
   */
  @Prop() doer: string;

  /**
   * Set the doer label
   */
  @Prop() doerLabel: string;

  /**
   * Set the Project Price
   */
  @Prop() projectPrice: string;

  /**
   * Set the project price label
   */
  @Prop() projectPriceLabel: string;

  /**
   * Set the tag label
   */
  @Prop() tagLabel: string;

  @Watch('images')
  private imagesWatcher(newValue: ResponsiveImages | string) {
    if (newValue) {
      this.imageParams.responsiveImages =
        typeof newValue === 'string'
          ? (JSON.parse(newValue) as ResponsiveImages)
          : newValue;
    }
  }

  @Watch('isConsent')
  onCheckedChange(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      this.loadVideo = newValue;
    }
  }

  private readonly mediaController = {
    image: () => this.renderImage(),
    'yt-video': () => this.renderYouTubeVideo(),
  };

  private renderImage() {
    if (this.images === undefined) {
      return;
    }
    return renderResponsiveImages2(this.imageParams);
  }

  private renderYouTubeVideo() {
    if (this.ytVideoParam === undefined) {
      return;
    }

    if (this.loadVideo) {
      return (
        <cms-youtube-video
          style={{
            '--cms-container-display': 'block',
          }}
          autostart={this.startYtVideo}
          isConsent={this.loadVideo}
          videoParam={this.ytVideoParam}
          videoTitle={this.ytVideoTitle}
          lazyLoad={false}
        />
      );
    }
  }

  private autostartVideo() {
    this.startYtVideo = true;
  }

  private renderTopImage() {
    if (this.topImage) {
      const topImageStyle = {};
      topImageStyle['--cms-key-visual-top-image-desktop'] =
        `url("${this.topImage}")`;
      topImageStyle['--cms-key-visual-top-image-mobile'] = `url("${
        this.topImageMobile ? this.topImageMobile : this.topImage
      }")`;

      return <div class='top-image' style={topImageStyle} />;
    }
    return null;
  }

  private renderTags() {
    return <cms-tags tags={this.keywords} label={this.tagLabel} />;
  }

  private renderProjectPlanning() {
    return (
      <cms-project-planning
        challenge={this.challenge}
        challengeLabel={this.challengeLabel}
        buildTime={this.buildTime}
        buildTimeLabel={this.buildTimeLabel}
        doer={this.doer}
        doerLabel={this.doerLabel}
        projectPrice={this.projectPrice}
        projectPriceLabel={this.projectPriceLabel}
      />
    );
  }

  connectedCallback() {
    this.loadVideo = this.isConsent;

    if (this.images) {
      this.imagesWatcher(this.images);
    }
  }

  componentWillLoad() {
    this.imageParams.altText = this.headline || 'Hornbach';
    this.imageParams.imageSizes = this.imageSizes;
  }

  render() {
    return (
      <Host>
        <div class={'key-visual-container'}>
          <div class={'key-visual-container-inner grid-item'}>
            <div class={'badge-wrapper'}>
              {this.badgeName ? (
                <cms-badge
                  theme='service'
                  size='badge-sm'
                  name={this.badgeName}
                />
              ) : null}
            </div>
            {this.topImage ? this.renderTopImage() : null}
            {this.headline ? (
              <cms-headline
                style={{
                  '--cms-spacing-top': '0',
                  '--cms-theme-text-color':
                    'var(--cms-semanticColors-base-strong-text)',
                }}
                headlineText={this.headline}
                headlineTag={this.headlineLevel}
                headlinePosition='left'
                headlineClass='headline'
                isAdaptive={true}
              />
            ) : null}

            {this.pageText !== undefined ? (
              <div class={'page-text-box'}>
                <div innerHTML={this.pageText} />
              </div>
            ) : null}

            {this.pageType === 'manual' && this.challenge !== undefined ? (
              <div class={'manual-info-box'}>
                {this.renderProjectPlanning()}
              </div>
            ) : null}

            {this.ctaUrl ? (
              <cms-text-button-link
                style={{
                  '--cms-theme-text-color':
                    'var(--cms-semanticColors-base-strong-text)',
                  '--cms-theme-border-color':
                    'var(--cms-semanticColors-transparency-strong-border)',
                }}
                url={this.ctaUrl}
                label={this.ctaLabel}
                buttonText={this.ctaButtonText}
              />
            ) : null}
          </div>
          <div class={'key-visual-container-inner grid-item'}>
            {this.mediaType === 'yt-video' && !this.loadVideo ? (
              <div
                onClick={() => {
                  this.autostartVideo();
                }}
                onKeyDown={(e) => {
                  if (isEnterKey(e) || e.key === ' ') {
                    this.autostartVideo();
                  }
                }}
                class='consent-container'
              >
                <slot name='hdp-consent' />
              </div>
            ) : null}

            {this.mediaType === 'image' || this.mediaType === 'yt-video'
              ? this.mediaController[this.mediaType]()
              : null}
          </div>
          {this.keywords === '[]' || this.keywords.length === 0 ? null : (
            <div class={'key-visual-container-inner grid-item'}>
              {this.renderTags()}
            </div>
          )}
        </div>
      </Host>
    );
  }
}
